/*global Konva, QRCode runes, vkVariantTextColor */
class VKCanvas extends HTMLElement {
  constructor() {
    super();
    this.containerWidth = this.offsetWidth;
    this.containerHeight = this.offsetWidth; //this.offsetHeight;
    this.mediaWidth = 495;
    this.mediaHeight = 495;
    this.mediaGallery = document.querySelector('vk-media-gallery slider-component');
    this.mediaSrc = `${this.dataset.backgroundImage}&width=${this.mediaWidth}`;
    this.textColor = null;
    this.defaultVariantId = this.dataset.variantId;

    this.customizationType = this.dataset.customizationType;

    // declare canvas stage
    this.stage = null;
    this.layer = null;
    this.backgroundLayer = null;
    this.overlayLayer = null;
    this.watermarkLayer = null;
    this.textGroup = null;

    this.qrData = null;
    this.svgConfig = null;
    this.bgImageConfig = null;

    this.fontFamilies = ['vk-font-80', 'vk-font-90', 'vk-font-100'];

    this.selectedFontFamily = this.fontFamilies[2];

    this.defaultSettings = {
      letterSpacing: 1.8, // this fixes issues with font going outside boundaries
      verticalAlign: 'middle',
      // strokeWidth: 0.1,
      // stroke: '#666',
      shadowColor: '#999',
      shadowBlur: 1,
      shadowOffsetX: 0.5,
      shadowOffsetY: 0.2,
      shadowOpacity: 0.9,
    };

    this.layersZIndex = {
      keytagImage: 10,
      backgroundImage: 2,
      image: 3,
      text: 4,
      qr: 5,
      overlay: 1,
    };

    this.overlayImageUrl = null;
    this.overlayWatermarkUrl = null;

    this.path = null;

    this.dev = false;

    this.init();
  }

  init() {
    // Create the canvas stage
    this.stage = new Konva.Stage({
      container: `VKCanvas-${this.dataset.section}`,
      width: this.mediaWidth,
      height: this.mediaWidth,
    });

    // Create the background layer
    this.backgroundLayer = this.setBackgroundLayer();
    this.stage.add(this.backgroundLayer);
    this.backgroundLayer.moveToBottom();

    // // TODO: Create the overlay layer based on config, check when we need to add this overlay
    this.overlayLayer = this.setOverlayLayer();
    this.stage.add(this.overlayLayer);
    this.overlayLayer.moveToTop();

    this.watermarkLayer = this.setWatermarkLayer();
    this.stage.add(this.watermarkLayer);
    this.watermarkLayer.moveToTop();
    this.watermarkLayer.hide();

    // Create a new Layer
    this.layer = new Konva.Layer();

    // Add the layer to the stage
    this.stage.add(this.layer);

    window.addEventListener('resize', this.fitStageIntoParentContainer.bind(this));
    this.fitStageIntoParentContainer();

    // append a blocker element to the container
    const blocker = document.createElement('div');
    blocker.classList.add('scroll-blocker');
    this.appendChild(blocker);
  }

  getChild(layer, childName) {
    return layer.getChildren((node) => node.name() === childName)[0];
  }

  /**
   * This function updates the settings of a Konva group that contains a text and a boundary shape
   * @param {Konva.Group} layersGroup - The Konva group that contains the text and boundary shape
   * @param {Object} layerSettings - An object that contains the settings for the text and boundary shape
   * @param {boolean} isDev - A boolean flag that indicates whether the code is running in development mode or not
   * @returns {Konva.Group} - The updated Konva group
   */
  updateLayerSettings(layersGroup, layerSettings, isDev = false) {
    // Destructure the properties of layerSettings
    const { path, background, canvas, fontFamily, letterSpacing, fontSize, lineHeight, textAlign, direction, watermark } = layerSettings;

    // Set the dev flag
    this.dev = isDev;
    // Set the path
    this.path = path;
    // Set the text color to a hexadecimal value that corresponds to the default variant's text color
    this.textColor = this.mapFontColorToHex(vkVariantTextColor[this.defaultVariantId]);
    // Set the fill color of the boundary shape based on the dev flag
    const fill = this.dev ? `${this.textColor.substring(0, 7)}20` : `transparent`;

    this.overlayImageUrl = background?.overlayImageUrl || null;
    if (this.overlayImageUrl) {
      // Set the Overlay Image URL
      this.setOverlayLayer();
    }

    this.overlayWatermarkUrl = watermark || null;
    if (this.overlayWatermarkUrl) {
      // Set the Watermark Image URL
      this.setWatermarkLayer();
    }

    // If the layersGroup is not provided, create a new Konva group with a boundary shape and a text object
    if (!layersGroup) {
      layersGroup = new Konva.Group();

      const boundary = this.path ? new Konva.Path({ name: 'boundary' }) : new Konva.Rect({ name: 'boundary' });
      const bgImageBoundary = new Konva.Rect({ name: 'bgImageBoundary' });
      const bgImage = new Konva.Image({ name: 'bgImage' });
      const bgImageCrop = new Konva.Image({ name: 'bgImageCrop' });
      const text = this.path ? new Konva.TextPath({ name: 'text' }) : new Konva.Text({ name: 'text' });
      const image = new Konva.Image({ name: 'image' });
      const qrImage = new Konva.Image({ name: 'qr' });

      this.layer.add(layersGroup);
      layersGroup.add(bgImageBoundary);
      layersGroup.add(bgImage);
      layersGroup.add(bgImageCrop);
      layersGroup.add(boundary);
      layersGroup.add(text);
      layersGroup.add(image);
      layersGroup.add(qrImage);
    }

    // Set the size attributes of the boundary shape and text object
    const sizeAttributes = { width: canvas.width, height: canvas.height };

    // Set the attributes of the boundary shape
    const boundaryAttributes = {
      ...sizeAttributes,
      fill,
    };

    // Set the attributes of the text object
    const textAttributes = {
      ...sizeAttributes,
      ...this.defaultSettings,
      fontFamily: fontFamily || this.selectedFontFamily,
      letterSpacing: letterSpacing || this.defaultSettings.letterSpacing,
      fontSize,
      lineHeight,
      direction,
      align: textAlign,
      fill: `${this.textColor}`,
    };

    // Set the attributes of the image object
    const imageAttributes = this.getImageAlignment(layersGroup, 'image', textAlign);

    // Set the attributes of the qr object
    const qrAttributes = this.getImageAlignment(layersGroup, 'qr', textAlign);

    if (this.qrData) {
      this.qrData.textAlign = textAlign;
    }

    if (this.svgConfig) {
      this.svgConfig.textAlign = textAlign;
    }

    // If the path is provided, set the data attribute of the boundary shape and text object to the path
    if (this.path) {
      boundaryAttributes.data = this.path;
      textAttributes.data = this.path;
    }

    // Set the size and position attributes of the Konva group
    layersGroup.setAttrs({ ...sizeAttributes, x: canvas.x, y: canvas.y });

    // Set the attributes of the text object and boundary shape
    this.getChild(layersGroup, 'boundary').setAttrs(boundaryAttributes);

    if (background) {
      // Set the attributes of the background image object
      const bgBoundaryAttributes = {
        width: background.width,
        height: background.height,
        x: background.x - canvas.x,
        y: background.y - canvas.y,
        fill, //fill: '#ea0a3a30',
        // zIndex: 0,
      };

      const bgImageAttributes = this.getImageAlignment(layersGroup, 'bgImage', textAlign, 'bgImageBoundary');
      this.getChild(layersGroup, 'bgImageBoundary').setAttrs(bgBoundaryAttributes);
      this.getChild(layersGroup, 'bgImage').setAttrs(bgImageAttributes);
      this.getChild(layersGroup, 'bgImageCrop').setAttrs(bgImageAttributes);
    }

    this.getChild(layersGroup, 'text').setAttrs(textAttributes);
    this.getChild(layersGroup, 'image').setAttrs(imageAttributes);
    this.getChild(layersGroup, 'qr').setAttrs(qrAttributes);

    // Return the updated Konva group
    return layersGroup;
  }

  /**
   * This function updates the text content of a canvas group element.
   * The canvas group is an object in HTML5 canvas that can contain multiple
   * child elements.
   *
   * @param {Object} canvasGroup - The canvas group element to update.
   * @param {String} text - The new text content to set.
   * @param {Boolean} isSizeChange - A boolean flag indicating whether the change in text size triggers a layout change.
   * @param {Array} allowedEmojis - An array of allowed emojis that can be used in the text.
   *
   * @returns {String} The fixed text to update the textarea with.
   */
  updateLayerText(canvasGroup, text, isSizeChange = false, allowedEmojis) {
    // If the canvas group is not defined, return immediately
    if (!canvasGroup) return;

    // Convert the new text to uppercase
    const sourceText = text.toUpperCase();

    // Find the text child element
    const textElement = this.getChild(canvasGroup, 'text');

    // Set the text content of the text element to the source text
    textElement.text(sourceText);

    // If the canvas group has a path property (i.e., it's a text path), adjust the text to fit the path
    if (this.path) {
      // Find the child element with the ID "boundary"
      const boundaryElement = this.getChild(canvasGroup, 'boundary');

      // Adjust the text to fit the path length
      const fixedPathText = this.fixPathLength(boundaryElement, textElement);

      // If the fixed text is different from the source text, update the layer text recursively
      if (textElement.text() !== fixedPathText) {
        return this.updateLayerText(canvasGroup, fixedPathText, isSizeChange, allowedEmojis);
      }

      // Otherwise, return the fixed text
      return fixedPathText;
    }

    // If the canvas group does not have a path property, adjust the text to fit the canvas width
    const canvasText = textElement.textArr.map((line) => line.text).join('\n');
    const fixedText = this.fixTextLineBreaks(canvasText, sourceText, allowedEmojis);

    // If the text requires a hard fix, update the layer text recursively with the fixed text
    if (fixedText.hardFix === true) {
      return this.updateLayerText(canvasGroup, fixedText.result, isSizeChange, allowedEmojis);
    }

    // If the text has been fixed, but the canvas text is still different from the fixed text,
    // check if the difference is due to a space character at the end of the fixed text, and if so,
    // keep the space character if the size change flag is false, and return the fixed text otherwise.
    if (canvasText !== fixedText.result) {
      if (fixedText.result.endsWith(' ') && isSizeChange === false) {
        return fixedText.result;
      }

      return this.updateLayerText(canvasGroup, fixedText.result.slice(0, -1), isSizeChange, allowedEmojis);
    }

    // If the canvas text is the same as the fixed text, return the fixed text
    return fixedText.result;
  }

  getImageAlignment(layersGroup, child, align, boundaryLayer = 'boundary') {
    const boundary = this.getChild(layersGroup, boundaryLayer);
    const image = this.getChild(layersGroup, child);

    return { x: this.calculateImageAlignment(boundary.x(), boundary.width(), image.width(), align) };
  }

  calculateImageAlignment(boundaryX, boundaryWidth, imageWidth, alignment) {
    let imageX = boundaryX;

    if (alignment === 'center') {
      // adjust horizontal position
      const boundaryCenterX = boundaryX + boundaryWidth / 2;
      imageX = boundaryCenterX - imageWidth / 2;
    } else if (alignment === 'right') {
      const boundaryRightX = boundaryX + boundaryWidth;
      imageX = boundaryRightX - imageWidth;
    }

    return imageX;
  }

  calculateImageRatioAndAlignment({ boundary, alignment, width, height }) {
    let newWidth, newHeight;
    const imageRatio = width / height;

    // get the boundary width and height
    const boundaryWidth = boundary.width();
    const boundaryHeight = boundary.height();

    const boundaryRatio = boundaryWidth / boundaryHeight;

    if (imageRatio > boundaryRatio) {
      // image is wider than boundary
      newWidth = boundaryWidth;
      newHeight = boundaryWidth / imageRatio;
    } else {
      // image is taller than boundary
      newWidth = boundaryHeight * imageRatio;
      newHeight = boundaryHeight;
    }

    // adjust vertical position
    const boundaryCenterY = boundary.y() + boundaryHeight / 2;
    const imageCenterY = boundaryCenterY - newHeight / 2;

    return {
      width: newWidth,
      height: newHeight,
      y: imageCenterY,
      x: this.calculateImageAlignment(boundary.x(), boundary.width(), newWidth, alignment),
    };
  }

  updateSVGColor(svg, color) {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(svg, 'image/svg+xml');
    // Traverse the SVG and change all fill and stroke attributes to black
    const elements = xmlDoc.querySelectorAll('[fill], [stroke]');
    const styledElements = xmlDoc.querySelectorAll('[style]');

    for (const element of elements) {
      // check if attribute fill is none
      if (element.hasAttribute('fill') && element.getAttribute('fill') !== 'none') {
        element.setAttribute('fill', color);
      }
      if (element.hasAttribute('stroke') && element.getAttribute('stroke') !== 'none') {
        element.setAttribute('stroke', color);
      }
    }

    for (const styledElement of styledElements) {
      // styledElement.setAttribute('style', `fill: ${color}`);
      if (styledElement.getAttribute('style').includes('fill')) {
        styledElement.style.fill = color;
      }
    }

    if (elements.length === 0 && styledElements.length === 0) {
      // If no elements were found, set the fill color attribute of the root element
      xmlDoc.documentElement.setAttribute('fill', color);
    }

    // Serialize the modified SVG back to text
    const modifiedSVG = new XMLSerializer().serializeToString(xmlDoc);

    return modifiedSVG;
  }

  async updateLayerImage(svgConfig) {
    // If the canvas group is not defined, return immediately
    this.svgConfig = svgConfig;

    if (!this.svgConfig.canvasGroup) return;

    const boundary = this.getChild(this.svgConfig.canvasGroup, 'boundary');

    const imagePreview = document.createElement('img');

    const modifiedSVG = this.updateSVGColor(this.svgConfig.svgContent, this.textColor);

    const dataURL = 'data:image/svg+xml,' + encodeURIComponent(modifiedSVG);
    imagePreview.src = dataURL;

    const loadImage = new Promise((resolve, reject) => {
      imagePreview.onload = () => {
        const width = imagePreview.naturalWidth;
        const height = imagePreview.naturalHeight;

        resolve({ width, height });
      };

      imagePreview.onerror = () => {
        reject('error');
      };
    });

    const { width, height } = await loadImage;

    this.getChild(this.svgConfig.canvasGroup, 'image').setAttrs({
      image: imagePreview,
      ...this.calculateImageRatioAndAlignment({ boundary, alignment: this.svgConfig.textAlign, width, height }),
    });

    imagePreview.remove();

    return this.svgConfig.canvasGroup;
  }

  calculateBgImageRatioAndAlignment({ boundary, alignment, width, height }) {
    let newWidth, newHeight;
    const imageRatio = width / height;

    // get the boundary width and height
    const boundaryWidth = boundary.width();
    const boundaryHeight = boundary.height();

    const boundaryRatio = boundaryWidth / boundaryHeight;

    const isSmallestThanBoundary = width < boundaryWidth && height < boundaryHeight;

    if (isSmallestThanBoundary) {
      newWidth = width;
      newHeight = height;
    } else if (imageRatio < boundaryRatio) {
      // image is wider than boundary
      newWidth = boundaryWidth;
      newHeight = boundaryWidth / imageRatio;
    } else {
      // image is taller than boundary
      newWidth = boundaryHeight * imageRatio;
      newHeight = boundaryHeight;
    }

    // adjust vertical position
    const boundaryCenterY = boundary.y() + boundaryHeight / 2;
    const imageCenterY = boundaryCenterY - newHeight / 2;
    const scaleRatio = width / newWidth;

    return {
      width: newWidth,
      height: newHeight,
      y: imageCenterY,
      x: this.calculateImageAlignment(boundary.x(), boundary.width(), newWidth, alignment),
      cropCalc: {
        x: 0,
        y: (height - boundary.height() * scaleRatio) / 2,
        width: newWidth * scaleRatio,
        height: boundary.height() * scaleRatio,
      },
    };
  }

  async updateLayerBgImage(bgImageConfig) {
    this.bgImageConfig = bgImageConfig;

    // If the canvas group is not defined, return immediately
    if (!this.bgImageConfig.canvasGroup) return;

    const bgImageBoundary = this.getChild(this.bgImageConfig.canvasGroup, 'bgImageBoundary');

    const imagePreview = document.createElement('img');

    imagePreview.src = this.bgImageConfig.imageFile;

    const loadImage = new Promise((resolve, reject) => {
      imagePreview.onload = () => {
        const width = imagePreview.naturalWidth;
        const height = imagePreview.naturalHeight;

        resolve({ width, height });
      };

      imagePreview.onerror = () => {
        reject('error');
      };
    });

    const { width, height } = await loadImage;

    const bgImageLayer = this.getChild(this.bgImageConfig.canvasGroup, 'bgImage');

    const imageRatioAndAlignment = this.calculateBgImageRatioAndAlignment({ boundary: bgImageBoundary, alignment: 'center', width, height });

    bgImageLayer.setAttrs({
      image: imagePreview,
      ...imageRatioAndAlignment,
    });

    const { cropCalc: crop } = imageRatioAndAlignment;

    bgImageLayer.setAttrs({
      width: bgImageBoundary.width(),
      height: bgImageBoundary.height(),
      x: bgImageBoundary.x(),
      y: bgImageBoundary.y(),
      crop,
    });

    imagePreview.remove();
    this.overlayLayer.moveToTop();
    this.watermarkLayer.moveToTop();
    bgImageBoundary.moveToTop();

    return this.bgImageConfig.canvasGroup;
  }

  base64ToBlob(base64Data, contentType) {
    // Check if the data is a valid Base64 string
    if (!base64Data || typeof base64Data !== 'string') {
      throw new Error('Invalid Base64 string provided');
    }

    // Extract the content type and data from the Base64 string
    const parts = base64Data.split(';base64,');
    const base64String = parts[1];

    // Decode the Base64 string into a Uint8Array
    const byteString = atob(base64String);
    const byteNumbers = new Array(byteString.length);
    for (let i = 0; i < byteString.length; i++) {
      byteNumbers[i] = byteString.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    // Create a Blob object
    const blob = new Blob([byteArray], { type: contentType });

    return blob;
  }

  async addCroppedImageToForm(base64Image, currentField) {
    const contentType = 'image/webp';
    // const imageDataURL = this.stage.toDataURL();
    const generatedImage = this.base64ToBlob(base64Image, contentType);

    // Get a reference to our file input
    // const fileInput = document.querySelector(`#BgImageCrop-${this.dataset.section}`);

    const imageFile = new File([generatedImage], 'image.png', {
      type: contentType,
      lastModified: new Date(),
    });

    // Create a DataTransfer to get a FileList
    const dataTransfer = new DataTransfer();
    dataTransfer.items.add(imageFile);
    currentField.files = dataTransfer.files;
  }

  async updateLayerBgImageCrop(bgImageConfig) {
    this.bgImageConfig = bgImageConfig;

    await this.addCroppedImageToForm(bgImageConfig.imageFile, bgImageConfig.currentField);

    // If the canvas group is not defined, return immediately
    if (!this.bgImageConfig.canvasGroup) return;

    const bgImageBoundary = this.getChild(this.bgImageConfig.canvasGroup, 'bgImageBoundary');

    const imagePreview = document.createElement('img');

    imagePreview.src = this.bgImageConfig.imageFile;

    const loadImage = new Promise((resolve, reject) => {
      imagePreview.onload = () => {
        const width = imagePreview.naturalWidth;
        const height = imagePreview.naturalHeight;

        resolve({ width, height });
      };

      imagePreview.onerror = () => {
        reject('error');
      };
    });

    const { width, height } = await loadImage;

    const bgImageLayer = this.getChild(this.bgImageConfig.canvasGroup, 'bgImageCrop');

    const imageRatioAndAlignment = this.calculateBgImageRatioAndAlignment({ boundary: bgImageBoundary, alignment: 'center', width, height });

    bgImageLayer.setAttrs({
      image: imagePreview,
      ...imageRatioAndAlignment,
    });

    const { cropCalc: crop } = imageRatioAndAlignment;

    bgImageLayer.setAttrs({
      width: bgImageBoundary.width(),
      height: bgImageBoundary.height(),
      x: bgImageBoundary.x(),
      y: bgImageBoundary.y(),
      crop,
    });

    imagePreview.remove();
    this.overlayLayer.moveToTop();
    this.watermarkLayer.moveToTop();
    bgImageBoundary.moveToTop();

    return this.bgImageConfig.canvasGroup;
  }

  updateLayerQR(canvasGroup, uploadedFile, initialAlignment) {
    if (!canvasGroup) return;
    const boundary = this.getChild(canvasGroup, 'boundary');
    const { image, width, height } = uploadedFile;

    this.getChild(canvasGroup, 'qr').setAttrs({
      image,
      ...this.calculateImageRatioAndAlignment({ boundary, alignment: initialAlignment, width, height }),
    });

    // Return the canvas group
    return canvasGroup;
  }

  async controlQrRender(dataURL, callback) {
    const imagePreview = document.createElement('img');

    const fetchImage = await fetch(dataURL);
    const blob = await fetchImage.blob();

    const reader = new FileReader();
    reader.onload = (event) => {
      imagePreview.src = event.target.result;
      // get width and height
      imagePreview.onload = () => {
        const width = imagePreview.naturalWidth;
        const height = imagePreview.naturalHeight;
        callback(imagePreview, width, height);
      };
    };
    reader.readAsDataURL(blob);
  }

  renderQRcode(qrData) {
    this.qrData = qrData;

    // if qrData.content is empty, clear the qr layer
    if (this.qrData.content === false) {
      this.getChild(this.qrData.canvasLabel, 'qr').setAttr('image', '');
      return;
    }

    const qrOptions = {
      margin: 0,
      color: {
        dark: this.textColor,
        light: '#0000',
      },
      width: 250,
      //width: this.qrData.width,
    };

    QRCode.toDataURL(this.qrData.content, qrOptions, (err, dataUrl) => {
      // console.log(dataUrl, this.qrData.textAlign);
      this.controlQrRender(dataUrl, (image, width, height) => {
        this.updateLayerQR(this.qrData.canvasLabel, { image, width, height }, this.qrData.textAlign);
      });
    });
  }

  /**
   * Fixes line breaks in the canvasText based on the sourceText and allowedEmojis.
   *
   * @param {string} canvasText - The text in canvas element.
   * @param {string} sourceText - The source text to compare with canvasText.
   * @param {Array} allowedEmojis - The array of allowed emojis.
   *
   * @returns {Object} An object with the fixed text and a flag indicating if it required hard-fix.
   */
  fixTextLineBreaks(canvasText, sourceText, allowedEmojis) {
    // Convert sourceText and canvasText into arrays of unicode characters.
    const sourceChars = runes(sourceText);
    const canvasChars = runes(canvasText);

    // Flag indicating if the fix required hard-fix.
    let hardFix = false;

    // Loop through each character in the sourceText and canvasText.
    for (const [i, sourceChar] of sourceChars.entries()) {
      const canvasChar = canvasChars[i];

      // If canvasChar is undefined or different from sourceChar.
      if (!canvasChar || canvasChar !== sourceChar) {
        // If canvasChar is line break or ends with line break.
        if (canvasChar === '\n' || (canvasChar && canvasChar.endsWith('\n'))) {
          // If sourceChar is space, replace it with line break.
          if (sourceChar === ' ') {
            sourceChars[i] = '\n';
          } else {
            // Otherwise, insert line break at the current position.
            sourceChars.splice(i, 0, '\n');
          }
          // Return the fixed text and set hard-fix flag to true.
          return { result: sourceChars.join(''), hardFix: true };
        } else if (allowedEmojis.includes(sourceChar) && sourceChar.startsWith(canvasChar)) {
          // If sourceChar is allowed emoji and starts with canvasChar, remove it.
          sourceChars.splice(i, 1);
          // Return the fixed text and set hard-fix flag to true.
          return { result: sourceChars.join(''), hardFix: true };
        }
      }

      // If canvasChar is undefined and sourceChar is space and there are no characters left after it.
      if (!canvasChar && sourceChar === ' ' && !sourceChars[i + 1]) {
        // Return the original text and set hard-fix flag to false.
        return { result: sourceChars.join(''), hardFix: false };
      } else if (!canvasChar) {
        // If canvasChar is undefined and sourceChar is not space, remove the last character.
        return { result: sourceChars.slice(0, -1).join(''), hardFix: true };
      }
    }

    // If everything is alright, return the original text with the hard-fix flag.
    const result = sourceChars.join('');
    return { result, hardFix };
  }

  isAllowedEmoji(char, allowedEmojis) {
    return allowedEmojis.includes(char);
  }

  fixPathLength(boundaryElement, textElement) {
    const pathLength = boundaryElement.getLength();
    // 0.97 is a magic number to make it work??
    const fullWidth = textElement.getTextWidth() + (textElement.text().length - 1) * textElement.letterSpacing() * 0.97;

    return fullWidth > pathLength ? textElement.text().slice(0, -1) : textElement.text();
  }

  showLayer(canvasGroup, childName) {
    canvasGroup.children.forEach((children) => {
      if (children.name() != 'boundary' && children.name() != 'bgImageBoundary') {
        children.hide();
      }
    });

    this.getChild(canvasGroup, childName).show();
    canvasGroup.draw();
  }

  setBackgroundLayer() {
    const backgroundLayer = this.backgroundLayer ? this.backgroundLayer : new Konva.Layer();
    const instance = this;

    const imageObj = new Image();
    imageObj.onload = function () {
      const bgImageNode = new Konva.Image({
        x: 0,
        y: 0,
        image: imageObj,
      });

      // add the image to the layer
      backgroundLayer.add(bgImageNode);

      // show the text groups after image has finished loading
      instance.layer.children.forEach((canvasGroup) => {
        canvasGroup.show();
      });
    };

    imageObj.src = this.mediaSrc;
    imageObj.crossOrigin = 'Anonymous';

    this.backgroundImage = imageObj;
    return backgroundLayer;
  }

  setOverlayLayer() {
    const overlayLayer = this.overlayLayer ? this.overlayLayer : new Konva.Layer({ name: 'overlay' });
    const instance = this;

    if (this.overlayImageUrl) {
      const imageObj = new Image();
      imageObj.onload = function () {
        const bgImageNode = new Konva.Image({
          x: 0,
          y: 0,
          image: imageObj,
        });

        // add the image to the layer
        overlayLayer.add(bgImageNode);

        // show the text groups after image has finished loading
        instance.layer.children.forEach((canvasGroup) => {
          canvasGroup.show();
        });
      };

      // console.log({ overlay: this.overlayImageUrl });
      imageObj.src = `${this.overlayImageUrl}&width=${this.mediaWidth}`;
      imageObj.crossOrigin = 'Anonymous';
    }

    return overlayLayer;
  }

  setWatermarkLayer(useWatermark = false) {
    const watermarkLayer = this.watermarkLayer ? this.watermarkLayer : new Konva.Layer({ name: 'overlay' });
    const instance = this;

    if (this.overlayWatermarkUrl) {
      const imageObj = new Image();
      imageObj.onload = function () {
        const bgImageNode = new Konva.Image({
          x: 0,
          y: 0,
          image: imageObj,
        });

        // add the image to the layer
        watermarkLayer.add(bgImageNode);

        // show the text groups after image has finished loading
        instance.layer.children.forEach((canvasGroup) => {
          canvasGroup.show();
        });
      };

      // console.log({ overlay: this.overlayImageUrl });
      imageObj.src = `${this.overlayWatermarkUrl}&width=${this.mediaWidth}`;
      imageObj.crossOrigin = 'Anonymous';
    }

    return watermarkLayer;
  }

  updateCanvas(variant) {
    // Get the image src from the media gallery , mediaId parameter is the id of the media gallery
    //const mediaGallery = this.closest('vk-media-gallery');
    //const testMediaSrc = mediaGallery.getActiveMedia().querySelector('img').src.split('&width=')[0] + `&width=${this.containerWidth}`;

    // Get the image src from the variant
    this.mediaSrc = `${variant.featured_media.preview_image.src}&width=${this.mediaWidth}`;

    // Get the text color from the global object
    this.defaultVariantId = variant.id;
    this.textColor = this.mapFontColorToHex(vkVariantTextColor[this.defaultVariantId]);

    this.layer.children.forEach((canvasGroup) => {
      // Hide the text groups until image has finished loading
      canvasGroup.hide();

      const textElement = this.getChild(canvasGroup, 'text');
      textElement.setAttr('fill', `${this.textColor}`);
      // textElement.setAttr('shadowColor', `${this.textColor}`);

      if (this.qrData) {
        this.renderQRcode(this.qrData);
      }

      if (this.svgConfig) {
        this.updateLayerImage(this.svgConfig);
      }

      const boundaryElement = this.getChild(canvasGroup, 'boundary');
      const labelFillColor = this.dev ? `${this.textColor.substring(0, 7)}20` : `transparent`;
      boundaryElement.setAttr('fill', labelFillColor);
    });

    // Update the background image
    this.setBackgroundLayer();
    // this.backgroundLayer.setZIndex(this.layersZIndex.keytagImage);

    // TODO: Update the overlay layer based on config
    this.setOverlayLayer();
    this.setWatermarkLayer();
    // this.overlayLayer.setZIndex(this.layersZIndex.overlay);
  }

  exportImageData(addWatermark = false) {
    // show watermark
    if (addWatermark) {
      this.watermarkLayer.show();
    }

    const contentType = 'image/png';
    // const imageDataURL = this.stage.toDataURL();
    const imageBlob = this.stage.toBlob({ pixelRatio: 2 });

    // Get a reference to our file input
    const fileInput = document.querySelector(`#CustomProductImage-${this.dataset.section}`);

    return imageBlob
      .then((generatedImage) => {
        // remove watermark
        if (addWatermark) {
          this.watermarkLayer.hide();
        }

        // Create a new File object
        const imageFile = new File([generatedImage], 'image.png', {
          type: contentType,
          lastModified: new Date(),
        });

        // Create a DataTransfer to get a FileList
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(imageFile);
        fileInput.files = dataTransfer.files;

        /* Show image preview --> remove
        const reader = new FileReader();
        reader.onload = (event) => {
          const imagePreview = document.createElement('img');
          imagePreview.src = event.target.result;
          imagePreview.classList.add('image-preview');
          document.querySelector('.tab-content[data-active-type=true] .image-upload-preview').appendChild(imagePreview);
        };
        reader.readAsDataURL(generatedImage);
        //--> remove*/

        return true;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.log(err);
        return false;
      });
  }

  generateURI() {
    return this.stage.toDataURL({ pixelRatio: 2 });
  }

  fitStageIntoParentContainer() {
    const mediaGalleryWidth = this.mediaGallery.offsetWidth;
    const scale = mediaGalleryWidth / this.mediaWidth;

    const newWidth = mediaGalleryWidth;
    const newHeight = mediaGalleryWidth;

    this.style.width = newWidth + 'px';
    this.style.height = newHeight + 'px';

    // console.log({mediaGalleryWidth, scale});

    this.stage.width(newWidth);
    this.stage.height(newHeight);
    this.stage.scale({ x: scale, y: scale });
    this.stage.draw();
  }

  mapFontColorToHex(variantData) {
    const { option, custom } = variantData;

    if (custom) {
      return custom;
    }

    const colorOptions = option.split('/');

    let colorOption = colorOptions[0].toLowerCase().split(' ').join('-'); // fix for "Glow in the Dark" version

    if (colorOptions.length > 1) {
      colorOption = colorOptions[1].toLowerCase().split(' ').join('-');
    }

    const colorMap = {
      'glow-in-the-dark': '#000009E6',
      dusk: '#000009E6', // Glow in the dark
      nighttime: '#000009E6', // Glow in the dark
      daylight: '#000009E6', // Glow in the dark
      black: '#000009E6',
      white: '#FFFFFFEF',
      red: '#A5373C', // Brushed Steel/Red
      green: '#076E59', // Mint/Green
      gold: '#D1AA66',
      blue: '#22406A', // needs fix?
      'sky-blue': '#22406A',
      syrah: '#790020',
      yellow: '#FFEA02',
      orange: '#E15616',
    };

    return colorMap[colorOption] || colorMap['white'];
  }
}

customElements.define('vk-canvas', VKCanvas);
